import type {
  IBulkSetBookingsDto,
  IBulkUpdateBookingsDateTimeDto,
} from "../dto";
import { BookingsPollingStatusEnum, DirectionEnum } from "../enums";
import {
  BookingConflictErrorType,
  BulkBookingCreateConflict,
  ErrorAdditionalT,
  IIneligibleAdditional,
  IPassengerIneligibleAdditional,
} from "./bookings.models";
import { ITranslatableItem, TInfoItem } from "./common.models";
import { DateYMDString } from "./date-time.models";
import { BookingEntity, RideEntity } from "./typeorm";

export interface IBulkSetBookingsInitPayload extends IBulkSetBookingsDto {
  companyId: number;
}

export interface IBulkUpdateBookingsDateTimeInitPayload extends IBulkUpdateBookingsDateTimeDto {
  companyId: number;
}

export interface IBookingsPollingAbortPayload {
  token: TBookingBulkToken;
}

export interface IBulkBookingsCheckStatusPayload {
  token: TBookingBulkToken;
}

export type TBookingBulkToken = `bookings_${"preview" | "create"}_${string}`;
export interface IBookingConflictError {
  id: string;
  passengerId: number;
  passengerFullName: string;
  displayDirection?: DirectionEnum;
  date: DateYMDString;
  errorType: BookingConflictErrorType;
  errorAdditional?:
    | Partial<Record<ErrorAdditionalT, RideEntity | BookingEntity>>
    | IPassengerIneligibleAdditional
    | IIneligibleAdditional;
}
export interface IBookingsPollingInitResult {
  token: TBookingBulkToken;
  state: TBookingsPollingState;
}

export interface IBookingsPollingCheckResult {
  token: TBookingBulkToken;
  state: TBookingsPollingState;
}

export interface IBookingsPollingAbortResult {
  token: TBookingBulkToken;
  state: TBookingsPollingState;
}

interface IBookingsPollingStateBase {
  logs?: TInfoItem[];
  debugInfo?: unknown;
  token?: TBookingBulkToken;
  isPreview?: boolean;
}

export interface IBookingsPollingStateInitializing extends IBookingsPollingStateBase {
  status: BookingsPollingStatusEnum.Initializing;
  totalCount: number;
  isPreview: boolean | undefined;
}

export interface IBookingsPollingStateInProgress extends IBookingsPollingStateBase {
  status: BookingsPollingStatusEnum.InProgress;
  current: number;
  newBookingsIds: number[];
}

export interface IBookingsPollingStateError extends IBookingsPollingStateBase {
  status: BookingsPollingStatusEnum.Error;
  error: IBookingPollingError;
  translatableError?: ITranslatableItem;
  passengerId?: number;
  date?: DateYMDString;
}

export interface IBookingsPollingStateConflictError extends IBookingsPollingStateBase {
  status: BookingsPollingStatusEnum.ConflictError;
  current?: number;
  conflictError?: IBookingConflictError;
  translatableError?: ITranslatableItem;
  passengerId?: number;
  date?: DateYMDString;
}

export interface IBookingsPollingStateCompleted extends IBookingsPollingStateBase {
  status: BookingsPollingStatusEnum.Completed;
  newBookingsIds: number[];
}

export interface IBookingsPollingStateAborted extends IBookingsPollingStateBase {
  status: BookingsPollingStatusEnum.Aborted;
  translatableError?: ITranslatableItem;
  current?: number;
  newBookingsIds?: number[];
  passengerId?: number;
  date?: DateYMDString;
  isAborted: boolean;
}

export interface IBookingsPollingStateIdle extends IBookingsPollingStateBase {
  status: BookingsPollingStatusEnum.Idle;
  translatableError?: ITranslatableItem;
}

export interface IBookingsPollingCombinedState extends IBookingsPollingStateBase {
  status: BookingsPollingStatusEnum;
  totalCount?: number;
  current?: number;
  newBookingsIds?: number[];
  error?: IBookingPollingError;
  conflictError?: IBookingConflictError;
  passengerId?: number;
  date?: DateYMDString;
  isAborted?: boolean;
  conflictErrors?: BulkBookingCreateConflict[];
  errors?: IBookingPollingError[];
  translatableErrors?: ITranslatableItem[];
}

export type TBookingsPollingState =
  | IBookingsPollingStateInitializing
  | IBookingsPollingStateError
  | IBookingsPollingStateConflictError
  | IBookingsPollingStateAborted
  | IBookingsPollingStateInProgress
  | IBookingsPollingStateCompleted
  | IBookingsPollingStateIdle;

export interface IAbortBookingPollingEventArgs {
  token: TBookingBulkToken;
}

export const defaultBookingsPollingState: IBookingsPollingCombinedState = {
  status: BookingsPollingStatusEnum.Idle,
  token: undefined,
  totalCount: undefined,
  current: undefined,
  newBookingsIds: undefined,
  error: undefined,
  passengerId: undefined,
  date: undefined,
  errors: undefined,
  translatableErrors: undefined,
  conflictError: undefined,
  conflictErrors: undefined,
  isAborted: undefined,
};

export interface IBookingsPollingUiState {
  isLoading: boolean;
  loadPercentage?: number;
  error?: IBookingPollingError | null;
  conflictError?: BulkBookingCreateConflict | null; //BookingBulkCreateStateConflictError;
  isPreview: boolean;
  isAborted?: boolean;
  newBookingsIds: number[];
  isFinished?: boolean;
  translatableError?: ITranslatableItem | null;
  conflictErrors?: BulkBookingCreateConflict[];
  errors?: IBookingPollingError[];
  translatableErrors?: ITranslatableItem[];
  totalCount?: number;
  current?: number;
}

export const defaultBookingBulkCreateState: IBookingsPollingUiState = {
  isLoading: false,
  isAborted: false,
  loadPercentage: 0,
  error: null,
  conflictError: null,
  isPreview: true,
  newBookingsIds: [],
  isFinished: false,
  translatableErrors: [],
  errors: [],
  conflictErrors: [],
  translatableError: null,
  totalCount: undefined,
  current: undefined,
};

export interface IBookingPollingError {
  error: unknown;
  passengerId: number;
  date: DateYMDString;
}
